import { Highlight, PredictionLabelType } from '@modules/feedback/types';
import { useTheme } from '@mui/system';
import emojiRegex from 'emoji-regex';
import { ReactNode, useEffect, useState } from 'react';
import OverlappingMarkup from '../FeedbackDetails/FeedbackDetailsConversation/OverlappingMarkup';
import FeedbackHighlight from './FeedbackHighlight';

const regex = emojiRegex();

interface FeedbackHighlighterProps {
  text: string;
  highlights: Highlight[];
  msgIndex?: number;
}

const FeedbackHighlighter = ({
  text,
  highlights,
  msgIndex,
}: FeedbackHighlighterProps) => {
  const theme = useTheme();
  const [emojis, setEmojis] = useState<
    {
      emoji: string;
      index: number | undefined;
    }[]
  >([]);

  useEffect(() => {
    if (!!text) {
      // Find all emojis in the text and store them in an array along with their
      // indices. This is used to highlight the update the startIDx and endIDx
      // of the highlights in the text.
      [...text?.matchAll(regex)].forEach((match) => {
        const emoji = {
          emoji: match[0],
          index: match.index,
        };

        return setEmojis((prevEmojis) => {
          return [...prevEmojis, emoji];
        });
      });
    }

    return () => {
      setEmojis([]);
    };
  }, [text]);

  const highlightedSpan = (
    highlight: Highlight,
    bgcolor: string,
    color: string,
  ) => {
    return {
      content: ({ children }: { children: ReactNode }) => {
        return (
          <FeedbackHighlight
            bgcolor={bgcolor}
            color={color}
            highlight={highlight}
            msgIndex={msgIndex}
          >
            {children}
          </FeedbackHighlight>
        );
      },
    };
  };

  const mapSentimentTypeToColor = (highlight: Highlight) => {
    if (highlight.labelType === PredictionLabelType.Reason) {
      return highlightedSpan(
        highlight,
        theme.palette.primary.main,
        theme.palette.primary.contrastText,
      );
    }

    switch (highlight.sentiment) {
      case 'Positive':
        return highlightedSpan(
          highlight,
          theme.palette.sentiment.positive,
          'common.white',
        );

      case 'Negative':
        return highlightedSpan(
          highlight,
          theme.palette.sentiment.negative,
          'common.white',
        );

      case 'Neutral':
        return highlightedSpan(
          highlight,
          theme.palette.sentiment.neutral,
          'common.white',
        );

      default:
        return highlightedSpan(
          highlight,
          theme.palette.primary.main,
          theme.palette.primary.contrastText,
        );
    }
  };

  if (!highlights?.length) {
    return <>{text}</>;
  }

  const styles = highlights.map((highlight) => {
    // Go through the emojis and find the start and end indices of the highlight
    // in the text. This is done by finding the index of the emoji that is
    // before the start of the highlight. Then, the start and end index of the
    // highlight is increased by the number of emojis that are before the start
    // of the highlight.
    let emojisBeforeHighlight = 0;

    emojis.forEach((emoji) => {
      if (emoji.index && emoji.index < highlight.startIDx) {
        emojisBeforeHighlight++;
      }
    });

    return {
      id: highlight.id,
      min: highlight.startIDx + emojisBeforeHighlight,
      max: highlight.endIDx + emojisBeforeHighlight,
      style: mapSentimentTypeToColor(highlight),
    };
  });

  return <OverlappingMarkup text={text} styling={styles} />;
};

export default FeedbackHighlighter;
